<script setup lang="ts">
  import { useSlots, watch } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
  } from '../alert-dialog';
  import type { ButtonVariants } from '../button';

  interface Props {
    cancelText?: string;
    confirmText?: string;
    confirmVariant?: ButtonVariants['variant'];
    contentClass?: HTMLAttributes['class'];
    overlayClass?: HTMLAttributes['class'];
  }

  withDefaults(defineProps<Props>(), {
    cancelText: 'Cancel',
    confirmText: 'Confirm',
  });
  const emits = defineEmits<{
    (e: 'confirm'): void;
    (e: 'decline'): void;
    (e: 'close'): void;
  }>();

  const slots = useSlots();

  const open = defineModel<boolean>();

  watch(open, () => {
    if (!open.value) emits('close');
  });
</script>

<template>
  <AlertDialog v-model:open="open">
    <AlertDialogTrigger v-if="slots.trigger" as-child>
      <slot name="trigger" />
    </AlertDialogTrigger>

    <AlertDialogContent :class="contentClass" :overlay-class="overlayClass">
      <AlertDialogHeader>
        <AlertDialogTitle v-if="slots.title">
          <slot name="title" />
        </AlertDialogTitle>

        <AlertDialogDescription v-if="slots.description">
          <slot name="description" />
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel @click="emits('decline')">
          {{ cancelText }}
        </AlertDialogCancel>

        <AlertDialogAction :variant="confirmVariant" @click="emits('confirm')">
          {{ confirmText }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped></style>
